import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { useLocation, useNavigate, useRouteError } from 'react-router';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';

import { Wordmark } from '@/components/Wordmark';
import { brand } from '@/theme/brand';
import {
  classifyNotFound,
  classifyRouteError,
  isGuestPath,
  type ClassifiedError,
} from './classifyRouteError';

export function RouteErrorPage() {
  const error = useRouteError();
  const location = useLocation();
  const audience = isGuestPath(location.pathname) ? 'guest' : 'app';
  const classified = classifyRouteError(error, { audience });

  useEffect(() => {
    console.error('[Menuvera]', error);
  }, [error]);

  return (
    <ErrorScene
      classified={classified}
      path={location.pathname}
      audience={audience}
      onHome={() => window.location.assign('/')}
      onSignIn={() => window.location.assign('/login')}
    />
  );
}

export function NotFoundPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const audience = isGuestPath(location.pathname) ? 'guest' : 'app';

  return (
    <ErrorScene
      classified={classifyNotFound(audience)}
      path={location.pathname}
      audience={audience}
      onHome={() => void navigate('/')}
    />
  );
}

/**
 * Catches render errors outside the router (providers, boot). Route errors
 * go through `errorElement` instead — this is the last net under that.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Menuvera]', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <ErrorScene
        classified={classifyRouteError(this.state.error, { audience: 'app' })}
        path={typeof window !== 'undefined' ? window.location.pathname : '/'}
        audience="app"
        onHome={() => window.location.assign('/')}
        onSignIn={() => window.location.assign('/login')}
      />
    );
  }
}

export function ErrorScene({
  classified,
  path,
  audience,
  onHome,
  onSignIn,
}: {
  classified: ClassifiedError;
  path: string;
  audience: 'app' | 'guest';
  onHome?: () => void;
  onSignIn?: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const showCode = classified.status >= 400;
  const showDetails = audience === 'app' || import.meta.env.DEV;
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;
  const primaryIsReload = classified.kind !== 'not-found';

  useEffect(() => {
    const previous = document.title;
    document.title = `${classified.status >= 400 ? classified.status : 'Error'} · ${brand.name}`;
    return () => {
      document.title = previous;
    };
  }, [classified.status]);

  const report = formatReport(classified, path);

  const copyReport = async () => {
    const ok = await copyText(report);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.12fr) minmax(0, 0.88fr)' },
        bgcolor: brand.palette.paper,
        '@keyframes errorRise': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes errorFade': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        '@keyframes errorLeaf': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-8px)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '@keyframes errorRise': { from: { opacity: 1 }, to: { opacity: 1 } },
          '@keyframes errorFade': { from: { opacity: 1 }, to: { opacity: 1 } },
          '@keyframes errorLeaf': {
            '0%, 100%': { transform: 'none' },
            '50%': { transform: 'none' },
          },
        },
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 0,
          p: { md: 4, lg: 5 },
          bgcolor: brand.palette.carbon,
          color: brand.palette.paper,
          animation: 'errorFade 700ms ease both',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 80% 60% at 18% 12%, rgba(255,255,255,0.07), transparent 55%), radial-gradient(ellipse 70% 50% at 92% 88%, rgba(255,255,255,0.045), transparent 50%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            opacity: 0.28,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 75% 70% at 50% 45%, black, transparent)',
            pointerEvents: 'none',
          },
        }}
      >
        <Wordmark size={22} inverted sx={{ position: 'relative', zIndex: 1, flexShrink: 0 }} />

        {showCode && (
          <Typography
            aria-hidden
            sx={{
              position: 'absolute',
              right: { md: -24, lg: -8 },
              bottom: { md: -36, lg: -48 },
              fontFamily: brand.displayFamily,
              fontSize: { md: 220, lg: 280 },
              fontWeight: 500,
              letterSpacing: '-0.06em',
              lineHeight: 0.8,
              color: 'rgba(255,255,255,0.06)',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {classified.status}
          </Typography>
        )}

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            py: 4,
          }}
        >
          <Box
            sx={{
              animation: 'errorLeaf 7s ease-in-out infinite',
              filter: 'drop-shadow(0 28px 40px rgba(0,0,0,0.45))',
            }}
          >
            <TornLeaf status={showCode ? classified.status : null} />
          </Box>
        </Box>

        <Typography
          sx={{
            position: 'relative',
            zIndex: 1,
            color: 'rgba(255,255,255,0.38)',
            fontSize: 12,
            letterSpacing: '0.02em',
            maxWidth: 280,
          }}
        >
          {brand.tagline}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 3, sm: 5, md: 6, lg: 8 },
          py: { xs: 6, sm: 7 },
          bgcolor: brand.palette.paper,
          backgroundImage:
            'radial-gradient(ellipse 90% 55% at 50% -10%, rgba(32,32,32,0.035), transparent 60%)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            mx: 'auto',
            animation: 'errorRise 560ms cubic-bezier(0.2, 0.7, 0.2, 1) both',
          }}
        >
          <Box sx={{ display: { md: 'none' }, mb: 4 }}>
            <Wordmark size={22} />
          </Box>

          <Typography
            sx={{
              mb: 1.5,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: brand.palette.ash,
            }}
          >
            {statusLabel(classified)}
          </Typography>

          <Typography
            component="h1"
            sx={{
              mb: 1.75,
              fontFamily: brand.displayFamily,
              fontSize: { xs: 28, sm: 34 },
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1.15,
              color: brand.palette.ink,
            }}
          >
            {classified.title}
          </Typography>

          <Typography
            sx={{
              mb: 4,
              color: brand.palette.ash,
              fontSize: 15,
              lineHeight: 1.6,
              letterSpacing: '0.02em',
              maxWidth: 380,
            }}
          >
            {classified.description}
          </Typography>

          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
            {primaryIsReload && (
              <Button variant="contained" size="large" onClick={() => window.location.reload()}>
                {classified.kind === 'stale-client' ? 'Refresh' : 'Try again'}
              </Button>
            )}
            {classified.kind === 'unauthorized' && audience === 'app' && onSignIn && (
              <Button
                variant={primaryIsReload ? 'outlined' : 'contained'}
                size="large"
                onClick={onSignIn}
              >
                Sign in
              </Button>
            )}
            {audience === 'app' && onHome && classified.kind !== 'unauthorized' && (
              <Button
                variant={primaryIsReload ? 'outlined' : 'contained'}
                size="large"
                onClick={onHome}
              >
                Take me home
              </Button>
            )}
            {canGoBack && classified.kind === 'not-found' && (
              <Button variant="text" size="large" onClick={() => window.history.back()}>
                Go back
              </Button>
            )}
          </Stack>

          {showDetails && (
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${brand.palette.whisper}` }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography
                  sx={{
                    fontFamily: brand.monoFamily,
                    fontSize: 11,
                    color: brand.palette.fog,
                    letterSpacing: '0.06em',
                  }}
                >
                  Ref · {classified.digest}
                </Typography>
                <Button size="small" variant="text" onClick={() => void copyReport()}>
                  {copied ? 'Copied' : 'Copy details'}
                </Button>
              </Stack>

              <Button
                size="small"
                variant="text"
                onClick={() => setDetailsOpen((open) => !open)}
                sx={{ px: 0, minWidth: 0, color: brand.palette.graphite }}
              >
                {detailsOpen ? 'Hide technical details' : 'Technical details'}
              </Button>

              <Collapse in={detailsOpen}>
                <Box
                  component="pre"
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    m: 0,
                    maxHeight: 220,
                    overflow: 'auto',
                    borderRadius: `${brand.radius.cards}px`,
                    bgcolor: brand.palette.whisper,
                    color: brand.palette.slate,
                    fontFamily: brand.monoFamily,
                    fontSize: 11,
                    lineHeight: 1.5,
                    letterSpacing: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {report}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function statusLabel(classified: ClassifiedError): string {
  if (classified.kind === 'stale-client') return 'Update ready';
  if (classified.kind === 'offline') return 'Offline';
  if (classified.status >= 400) return `Error ${classified.status}`;
  return 'Error';
}

function formatReport(classified: ClassifiedError, path: string): string {
  const when = new Date().toISOString();
  const lines = [
    `Menuvera error`,
    `Ref: ${classified.digest}`,
    `Path: ${path}`,
    `Time: ${when}`,
    classified.status >= 400 ? `Status: ${classified.status}` : `Kind: ${classified.kind}`,
    '',
    classified.detail,
  ];
  if (classified.stack) lines.push('', classified.stack);
  return lines.join('\n');
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      textarea.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

function TornLeaf({ status }: { status: number | null }) {
  return (
    <Box sx={{ position: 'relative', width: 280, height: 360 }}>
      <Box
        sx={{
          position: 'absolute',
          inset: '18px 8px 8px 28px',
          bgcolor: '#ebe4d6',
          clipPath:
            'polygon(0 0, 92% 0, 96% 8%, 88% 16%, 98% 28%, 90% 40%, 100% 54%, 86% 66%, 96% 80%, 88% 92%, 78% 100%, 0 100%)',
          transform: 'rotate(-12deg)',
          opacity: 0.55,
        }}
      />
      <svg
        width="280"
        height="360"
        viewBox="0 0 280 360"
        fill="none"
        aria-hidden
        style={{ position: 'relative', display: 'block', transform: 'rotate(-6deg)' }}
      >
        <path
          d="M28 18h148l8 22-16 18 20 28-14 24 18 28-24 26 20 34-16 28 22 36-14 28-18 22H28c-8 0-14-6-14-14V32c0-8 6-14 14-14Z"
          fill="#f6f1e8"
        />
        <path
          d="M28 18h148l8 22-16 18 20 28-14 24 18 28-24 26 20 34-16 28 22 36-14 28-18 22H28c-8 0-14-6-14-14V32c0-8 6-14 14-14Z"
          stroke="rgba(32,32,32,0.12)"
          strokeWidth="1"
        />
        <text
          x="42"
          y="52"
          fill="#838383"
          fontSize="9"
          fontFamily={brand.fontFamily}
          letterSpacing="0.22em"
        >
          TONIGHT
        </text>
        <line x1="42" y1="68" x2="168" y2="68" stroke="rgba(32,32,32,0.12)" />
        {[0, 1, 2, 3, 4, 5].map((row) => {
          const y = 96 + row * 32;
          const width = row === 2 ? 78 : 108 - row * 4;
          return (
            <g key={row}>
              <rect x="42" y={y} width={width} height="7" rx="3.5" fill="rgba(32,32,32,0.1)" />
              <rect x="168" y={y} width="22" height="7" rx="3.5" fill="rgba(32,32,32,0.08)" />
              {row !== 5 && (
                <rect
                  x="42"
                  y={y + 14}
                  width={width * 0.72}
                  height="4"
                  rx="2"
                  fill="rgba(32,32,32,0.06)"
                />
              )}
            </g>
          );
        })}
        {status != null && (
          <text
            x="42"
            y="332"
            fill="#bdbdbd"
            fontSize="11"
            fontFamily={brand.monoFamily}
            letterSpacing="0.08em"
          >
            {String(status).padStart(3, '0')}
          </text>
        )}
      </svg>
    </Box>
  );
}
